import { createGlobalStyle, css } from 'styled-components';

import colors from '@rewardopl/styles/colors';
import colorsRgb from '@rewardopl/styles/colors_rgb';
import gradients from '@rewardopl/styles/gradients';
import shadows from '@rewardopl/styles/shadows';
import variables from '@rewardopl/styles/variables';

import { Button } from '@rewardopl/react-ui/button/index.styles';
import { Label } from '@rewardopl/react-ui/fieldset/index.styles';
import { Link } from '@rewardopl/react-ui/link_button/index.styles';

import bubblegumWoff2 from '../static/fonts/bubblegum.woff2';
import poppinsWoff2 from '../static/fonts/poppins.woff2';
import poppinsItalicWoff2 from '../static/fonts/poppins_italic.woff2';

const fonts = [
  {
    fontFamily: 'BubbleGum',
    fontStyle: 'normal',
    fontWeight: '400',
    src: bubblegumWoff2,
  },
  {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: '100 900',
    src: poppinsWoff2,
  },
  {
    fontFamily: 'Poppins',
    fontStyle: 'italic',
    fontWeight: '100 900',
    src: poppinsItalicWoff2,
  },
];

const fontStyle = css`
  ${fonts.map(
    (font) => css`
      @font-face {
        font-family: ${font.fontFamily};
        font-display: swap;
        src:
          url(${font.src}) format('woff2 supports variations'),
          url(${font.src}) format('woff2-variations');
        font-style: ${font.fontStyle};
        font-weight: ${font.fontWeight};
      }
    `,
  )}
`;

const style = document.createElement('style');
style.innerHTML = fontStyle.join('');
document.head.appendChild(style);

export const linkStyle = css`
  color: var(--link);
  font: inherit;
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &:focus {
    text-decoration: underline;
  }

  &.primary {
    font: inherit;
    font-weight: 700;

    &:hover,
    &:focus {
      color: var(--primary-dark);
      text-decoration: none;
    }
  }
`;

function addUnit(name: string, value: string | number): string | number {
  if (!name.endsWith('-opacity') && typeof value === 'number') {
    return `${value}px`;
  }

  return value;
}

const vars = css`
  ${Object.entries({
    ...colors,
    ...gradients,
    ...shadows,
  }).map(([name, value]) => `--${name}: ${value};`)}
`;

const unitVars = css`
  ${Object.entries(variables).map(([name, value]) => `--${name}: ${addUnit(name, value)};`)}
`;

const rgbVars = css`
  ${Object.entries(colorsRgb).map(([name, value]) => `--${name}-rgb: ${value};`)}
`;

export const GlobalStyle = createGlobalStyle`
  :root {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-smoothing: antialiased;
    -webkit-text-size-adjust: none;
    -moz-text-size-adjust: none;
    text-size-adjust: none;
    -webkit-tap-highlight-color: transparent;

    ${vars}
    ${unitVars}
    ${rgbVars}
  }

  body {
    min-height: 100vh;
    min-height: 100dvh;
    line-height: 1.333;
    margin: 0;
    overflow: hidden;
    background: white;
    color: var(--text);
    font-family: Poppins, sans-serif;
    font-weight: 400;
    font-size: 14px;
  }

  * {
    -webkit-user-select: none;
    user-select: none;
  }

  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 0;
    font-weight: 700;
  }

  h1 {
    font-size: 28px;
  }

  h2 {
    font-size: 20px;
  }

  h3 {
    font-size: 16px;
  }

  a:not([class*="sc-"]) {
    ${linkStyle}
  }

  ul,
  ol {
    margin: 0;
    padding: 0;
    list-style-position: inside;
  }

  button {
    margin: 0;
    cursor: pointer;
  }

  ${Link}, ${Button} {
    font-weight: 700;
  }

  hr {
    width: 100%;
    border: 0;
    border-top: 1px solid gray;
  }

  label {
    display: block;
  }

  ${Label} {
    font-weight: bold;
  }

  table {
    border-collapse: separate;
    border-spacing: 24px 10px;
    margin: -10px -24px;

    th {
      margin-bottom: 4px;
      text-align: left;
      font-weight: 600;
      vertical-align: top;

      @media screen and (min-width: 769px) {
        margin-bottom: 0;
      }
    }

    th,
    td {
      display: block;

      @media screen and (min-width: 769px) {
        display: table-cell;
      }
    }

    tr:not(:first-child) {
      th {
        margin-top: 10px;

        @media screen and (min-width: 769px) {
          margin-top: 0;
        }
      }
    }
  }
`;
